import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Formik } from "formik";
import { Checkbox, CircularProgress, Grid } from "@mui/material";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import GradientBorder from "examples/GradientBorder";
import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import bgSignIn from "assets/images/sarto-bg.png";
import { ResetPasswordSchema } from "validations";
import { resetPassword } from "store/slice/userSlice";
import useCommon from "hooks/useCommon";
import VuiAlert from "components/VuiAlert";
import NotFound from "layouts/errors/NotFound";

function ResetPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { DISPATCH, STORE, NAVIGATE } = useCommon();

  const { resetPasswordLoading, resetPasswordSuccess, resetPasswordError, resetPasswordMessage } =
    STORE((state) => state.user);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await DISPATCH(
        resetPassword({
          token,
          password: values.password,
        })
      ).unwrap();

      setTimeout(() => {
        NAVIGATE("/authentication/sign-in");
      }, 2000);
    } catch (err) {
      console.log(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) return <NotFound />;

  return (
    <CoverLayout
      image={bgSignIn}
      title="Reset Password"
      color="light"
      description="Enter your new password"
      cardContent
    >
      <Grid container justifyContent="center" alignItems="center">
        <Grid
          item
          xs={12}
          sm={8}
          md={10}
          lg={12}
          sx={{ display: "flex", justifyContent: "center", flexDirection: "column" }}
        >
      <Formik
        initialValues={{ password: "", confirmPassword: "" }}
        validationSchema={ResetPasswordSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
          <VuiBox
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              maxWidth: "420px",
              p: { xs: 3, sm: 4, md: "25px", lg: "40px" },
              background: "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05))",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: borders.borderRadius.lg,
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            }}
          >
            {resetPasswordSuccess && (
              <VuiAlert color="success" size="sm">
                {resetPasswordMessage}
              </VuiAlert>
            )}

            {resetPasswordError && (
              <VuiAlert color="error" size="sm">
                {resetPasswordError}
              </VuiAlert>
            )}

            {/* PASSWORD */}
            <VuiBox mb={2}>
              <VuiBox mb={1} ml={0.5}>
                <VuiTypography component="label" variant="button" color="light" fontWeight="medium">
                  New Password
                </VuiTypography>
              </VuiBox>

              <GradientBorder
                minWidth="100%"
                padding="1px"
                borderRadius={borders.borderRadius.lg}
                backgroundImage={radialGradient(
                  palette.gradients.borderLight.main,
                  palette.gradients.borderLight.state,
                  palette.gradients.borderLight.angle
                )}
              >
                <VuiInput
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={resetPasswordLoading || resetPasswordSuccess}
                  sx={({ typography: { size } }) => ({
                    fontSize: size.sm,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05))",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    WebkitBackdropFilter: "blur(20px)",
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.25)",
                    "& input": {
                      color: "#fff !important",
                      WebkitTextFillColor: "#fff !important",
                    },
                  })}
                />
              </GradientBorder>
              {touched.password && errors.password && (
                <VuiTypography variant="caption" color="error">
                  {errors.password}
                </VuiTypography>
              )}
            </VuiBox>

            {/* CONFIRM PASSWORD */}
            <VuiBox mb={3}>
              <VuiBox mb={1} ml={0.5}>
                <VuiTypography component="label" variant="button" color="light" fontWeight="medium">
                  Confirm Password
                </VuiTypography>
              </VuiBox>

              <GradientBorder
                minWidth="100%"
                padding="1px"
                borderRadius={borders.borderRadius.lg}
                backgroundImage={radialGradient(
                  palette.gradients.borderLight.main,
                  palette.gradients.borderLight.state,
                  palette.gradients.borderLight.angle
                )}
              >
                <VuiInput
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={resetPasswordLoading || resetPasswordSuccess}
                  sx={({ typography: { size } }) => ({
                    fontSize: size.sm,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05))",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    WebkitBackdropFilter: "blur(20px)",
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.25)",
                    "& input": {
                      color: "#fff !important",
                      WebkitTextFillColor: "#fff !important",
                    },
                  })}
                />
              </GradientBorder>
              {touched.confirmPassword && errors.confirmPassword && (
                <VuiTypography variant="caption" color="error">
                  {errors.confirmPassword}
                </VuiTypography>
              )}
            </VuiBox>

            {!resetPasswordSuccess && (
              <>
              <VuiBox component="label" display="flex" alignItems="center" mt={1}>
                <Checkbox
                  checked={showPassword}
                  onChange={(event) => setShowPassword(event.target.checked)}
                  size="small"
                  sx={{
                    padding: "2px !important",
                    "&.Mui-checked": { color: "#fff !important" },
                  }}
                />
                <VuiTypography variant="button" color="light" sx={{ fontSize: "12px", opacity: 0.85 }}>
                  Show password
                </VuiTypography>
              </VuiBox>
              <VuiBox mt={4}>
              <VuiButton
                color="light"
                variant="outlined"
                fullWidth
                type="submit"
                disabled={resetPasswordLoading || resetPasswordSuccess}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                {resetPasswordLoading && <CircularProgress size={16} color="inherit" />}
                {resetPasswordLoading
                  ? "Updating..."
                  : resetPasswordSuccess
                    ? "Password Updated ✓"
                    : "Reset Password"}
              </VuiButton>
              </VuiBox>
              </>
            )}
          </VuiBox>
        )}
      </Formik>
        </Grid>
      </Grid>
    </CoverLayout>
  );
}

export default ResetPassword;
